import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
    Plus, Trash2, Download, ArrowLeft, FilePlus2, CheckCircle2, GitCompare,
    FileText, TrendingUp, Copy, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import PartEditDialog from "@/components/PartEditDialog";
import InvestmentBreakdownPanel from "@/components/InvestmentBreakdownPanel";
import UomSelect, { uomPriceFactor, uomConvertValue } from "@/components/UomSelect";
import MasterCombobox from "@/components/MasterCombobox";
import AssemblyManager from "@/components/AssemblyManager";
import DraftEditor from "@/components/DraftEditor";
import CompareDrafts from "@/components/CompareDrafts";
import {
    Tooltip, ResponsiveContainer, Cell,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";

// Cost breakup chart palette — friendlier, no red, no black. Indigo + teal +
// amber + violet + steel-blue + lime. Distinct enough for stacked bars and
// pie slices, but visually calmer than the prior red+black accents.
const CHART_COLORS = ["#002FA7", "#0EA5E9", "#F59E0B", "#2A9D8F", "#9C27B0", "#84CC16"];

export default function PartDetail() {
    const { partId } = useParams();
    const navigate = useNavigate();
    const [part, setPart] = useState(null);
    const [drafts, setDrafts] = useState([]);
    const [activeDraftId, setActiveDraftId] = useState(null);
    const [activeMode, setActiveMode] = useState("quotation");  // quotation | zbc
    const firstLoadDone = useRef(false);

    // Reset all state when partId changes (critical when navigating between assembly ↔ child costing)
    useEffect(() => {
        firstLoadDone.current = false;
        setActiveDraftId(null);
        setActiveMode("quotation");
        setChildrenRollup(null);
        setPart(null);
        setDrafts([]);
        setTotalsByDraft({});
    }, [partId]);
    const [materials, setMaterials] = useState([]);
    const [bopMaster, setBopMaster] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [machines, setMachines] = useState([]);
    const [manpower, setManpower] = useState([]);
    const [newDraftOpen, setNewDraftOpen] = useState(false);
    const [newDraftName, setNewDraftName] = useState("");
    const [totalsByDraft, setTotalsByDraft] = useState({});
    // Bumped after each successful save so the authoritative backend totals are
    // re-fetched only once the edit has actually persisted (see flushSave).
    const [totalsVersion, setTotalsVersion] = useState(0);
    const [childrenRollup, setChildrenRollup] = useState(null);
    const [compareOpen, setCompareOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [assemblyOpen, setAssemblyOpen] = useState(false);

    const loadAll = useCallback(async () => {
        const [partRes, draftRes, mat, bop, ven, mach, mp] = await Promise.all([
            api.get(`/parts/${partId}`),
            api.get(`/parts/${partId}/drafts`),
            api.get("/materials"),
            api.get("/bop"),
            api.get("/vendors"),
            api.get("/machines"),
            api.get("/manpower"),
        ]);
        setPart(partRes.data);
        setDrafts(draftRes.data);
        setMaterials(mat.data);
        setBopMaster(bop.data);
        setVendors(ven.data);
        setMachines(mach.data);
        setManpower(mp.data);
        if (draftRes.data.length > 0 && !firstLoadDone.current) {
            firstLoadDone.current = true;
            // First load only: pick first draft and align mode to it
            const first = draftRes.data[0];
            setActiveDraftId(first.draft_id);
            setActiveMode(first.mode || "quotation");
        }
    }, [partId, activeDraftId, activeMode]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const computeTotalsLocal = (d, annualVolume, projectYears, childRollup) => {
        let rmTotal, bopTotal, procTotal, outTotal;
        if (d.direct_cost_mode) {
            rmTotal = Number(d.direct_rm_total) || 0;
            bopTotal = Number(d.direct_bop_total) || 0;
            procTotal = Number(d.direct_process_total) || 0;
            outTotal = Number(d.direct_outsource_total) || 0;
        } else {
            rmTotal = (d.materials || []).reduce((s, m) => {
                const price = Number(m.price_per_unit) || 0;
                const priceUnit = m.unit || "";
                const weightUnit = m.weight_unit || priceUnit;
                const qRaw = m.quantity;
                const qty = (qRaw && Number(qRaw) > 0) ? Number(qRaw) : 1;
                let gw = Number(m.gross_weight) || 0;
                if (!gw) {
                    const nw = Number(m.net_weight) || 0;
                    const sw = Number(m.scrap_weight) || 0;
                    if (nw || sw) gw = nw + sw;
                }
                if (gw > 0) {
                    const gwInPriceUnit = uomConvertValue(gw, weightUnit, priceUnit);
                    return s + qty * gwInPriceUnit * price;
                }
                if (qRaw && Number(qRaw) > 0) return s + Number(qRaw) * price;
                const legacyQty = (Number(m.qty) || 0) * (1 + (Number(m.scrap_pct) || 0) / 100);
                return s + legacyQty * price;
            }, 0);
            bopTotal = (d.bop || []).reduce((s, b) => {
                const price = Number(b.price_per_unit) || 0;
                const qty = Number(b.quantity) || 0;
                const gw = Number(b.gross_weight) || 0;
                if (gw > 0 && b.unit) {
                    const gwInPriceUnit = uomConvertValue(gw, b.weight_unit || b.unit, b.unit);
                    const q = qty > 0 ? qty : 1;
                    return s + q * gwInPriceUnit * price;
                }
                return s + qty * price;
            }, 0);
            procTotal = (d.processes || []).reduce((s, p) => {
                const rate = (Number(p.mhr_rs_per_hr) || 0) + (Number(p.manpower_rate_per_hr_total) || 0);
                const perPc = rate * (Number(p.cycle_time_sec) || 0) / 3600;
                const q = Number(p.quantity); const qty = (!q || q <= 0) ? 1 : q;
                return s + perPc * qty;
            }, 0);
            outTotal = (d.outsource || []).reduce((s, o) => {
                const price = (Number(o.price_per_unit) || 0) > 0 ? Number(o.price_per_unit) : (Number(o.price_per_piece) || 0);
                const q = Number(o.quantity); const qty = (!q || q <= 0) ? 1 : q;
                const gw = Number(o.gross_weight) || 0;
                if (gw > 0 && o.unit) {
                    const gwInPriceUnit = uomConvertValue(gw, o.weight_unit || o.unit, o.unit);
                    return s + qty * gwInPriceUnit * price;
                }
                return s + price * qty;
            }, 0);
        }
        // Child rollup contribution (assembly parts)
        const cr = childRollup || { rm_total: 0, bop_total: 0, process_total: 0, outsource_total: 0, amortised: 0, overhead_fixed: 0, profit_value: 0, investment: 0 };
        const subTotal = cr.rm_total + cr.bop_total + cr.process_total + cr.outsource_total + cr.amortised + cr.overhead_fixed + cr.profit_value;
        const childrenMfg = cr.rm_total + cr.bop_total + cr.process_total + cr.outsource_total;  // amort excluded from % bases

        const matTotal = rmTotal + bopTotal;
        const investmentTotal = (d.investments || []).reduce((s, i) => s + (Number(i.value) || 0), 0);
        const amortise = !!d.amortise_investment;
        const py = Number(projectYears) > 0 ? Number(projectYears) : 1;
        const lifetimeVol = annualVolume * py;
        const amortPerPiece = (amortise && lifetimeVol > 0) ? investmentTotal / lifetimeVol : 0;
        const cogsBase = matTotal + procTotal;
        const oh = d.overhead || {};
        const packing = Number(oh.packing) || 0;
        const transportation = Number(oh.transportation) || 0;
        // Honour the sequential_bases toggle (default TRUE = compounding, FALSE = flat parallel base)
        // Kept in-sync with backend/totals.py::compute_totals.
        const sequential = oh.sequential_bases !== false;
        // ICC base = RM + BOP (own + children) — same in both modes (no amort)
        const iccBase = matTotal + (cr.rm_total + cr.bop_total);
        const icc = oh.icc_mode === "pct" ? (Number(oh.icc_pct) || 0) * iccBase / 100 : (Number(oh.icc_value) || 0);
        const ownMfg = matTotal + procTotal + outTotal;  // no amort
        const parallelBase = ownMfg + childrenMfg;       // RM+BOP+Process+Outsource (own + children)
        const rejectionBase = sequential ? (parallelBase + icc) : parallelBase;
        const rejection = oh.rejection_mode === "value" ? (Number(oh.rejection_value) || 0) : (Number(oh.rejection_pct) || 0) * rejectionBase / 100;
        const toolMaintenanceBase = sequential ? (rejectionBase + rejection) : parallelBase;
        const toolMaintenance = oh.tool_maintenance_mode === "value" ? (Number(oh.tool_maintenance_value) || 0) : (Number(oh.tool_maintenance_pct) || 0) * toolMaintenanceBase / 100;
        const sgaBase = sequential ? (toolMaintenanceBase + toolMaintenance) : parallelBase;
        const sga = oh.sga_mode === "pct" ? (Number(oh.sga_pct) || 0) * sgaBase / 100 : (Number(oh.sga_value) || 0);
        const profitBase = sequential ? (sgaBase + sga) : parallelBase;
        const overheadFixed = packing + transportation + icc + sga + rejection + toolMaintenance;
        const profitPctRaw = Number(oh.profit_pct) || 0;
        let profitValue = oh.profit_mode === "value" ? (Number(oh.profit_value) || 0) : profitPctRaw * profitBase / 100;
        let profitPctDerived = profitPctRaw;

        // ZBC override
        const isZbc = d.mode === "zbc";
        const frozenPrice = Number(d.frozen_price) || 0;
        if (isZbc && frozenPrice > 0) {
            const nonProfit = matTotal + subTotal + procTotal + outTotal + amortPerPiece + overheadFixed;
            profitValue = frozenPrice - nonProfit;
            profitPctDerived = profitBase > 0 ? (profitValue / profitBase * 100) : 0;
        }

        // Display values — parent + children rollup (assembly view)
        const dispRm = rmTotal + cr.rm_total;
        const dispBop = bopTotal + cr.bop_total;
        const dispMat = dispRm + dispBop;
        const dispProc = procTotal + cr.process_total;
        const dispOut = outTotal + cr.outsource_total;
        const dispAmort = amortPerPiece + cr.amortised;
        const dispOverhead = overheadFixed + cr.overhead_fixed;
        const dispProfit = profitValue + cr.profit_value;
        const overheadTotal = dispOverhead + dispProfit;
        const finalCost = dispMat + dispProc + dispOut + dispAmort + dispOverhead + dispProfit;
        return {
            material_total: dispMat, rm_total: dispRm, bop_total: dispBop,
            process_total: dispProc, outsource_total: dispOut,
            total_investment: investmentTotal + cr.investment,
            amortised_investment_per_piece: dispAmort,
            lifetime_volume: lifetimeVol, project_years: py,
            icc, sga, rejection, tool_maintenance: toolMaintenance, packing, transportation,
            overhead_fixed: dispOverhead, profit_value: dispProfit,
            profit_pct: profitPctDerived,
            overhead_total: overheadTotal, final_cost: finalCost, cogs_base: cogsBase,
            // Value addition = conversion cost + margin (process + overhead + profit),
            // excluding bought-in RM/BOP/Outsource/Sub. Matches backend compute_totals;
            // this is only the instant-paint approximation — the backend value overrides.
            value_addition: dispProc + dispOverhead + dispProfit,
            sub_assemblies_total: 0,
            mode: d.mode || "quotation",
            frozen_price: frozenPrice,
            // own (parent-only) values for line-edit display sanity
            own_rm_total: rmTotal, own_bop_total: bopTotal, own_process_total: procTotal,
            own_outsource_total: outTotal, own_overhead_fixed: overheadFixed,
            own_profit_value: profitValue, own_amortised_per_piece: amortPerPiece,
            // children rollup sections (for "own vs children" badges)
            child_rm_total: cr.rm_total, child_bop_total: cr.bop_total,
            child_process_total: cr.process_total, child_outsource_total: cr.outsource_total,
            child_overhead_fixed: cr.overhead_fixed, child_profit_value: cr.profit_value,
            child_amortised_per_piece: cr.amortised,
        };
    };

    useEffect(() => {
        const annualVol = Number(part?.annual_volume) || 0;
        const projectYears = Number(part?.project_years) || 5;
        const map = {};
        drafts.forEach((d) => { map[d.draft_id] = computeTotalsLocal(d, annualVol, projectYears, childrenRollup); });
        setTotalsByDraft(map);
    }, [drafts, part?.annual_volume, part?.project_years, childrenRollup]);

    // Fetch backend-computed totals per draft — the SAME authority the costing
    // sheet uses (compute_totals via /totals). These override the local
    // approximation in `totalsByDraft`, so the comparison table and chart always
    // match each draft's own costing sheet (not a drifting frontend re-estimate).
    // Runs for every part, not just assemblies: for non-assembly parts the local
    // approximation and the backend agreed in theory but drifted on rounding and
    // the ZBC profit residual, which is exactly the "comparison total is wrong" bug.
    useEffect(() => {
        if (drafts.length === 0) return;
        // Don't fetch mid-edit: while a save is pending/in-flight the backend still
        // holds the OLD draft, so its /totals would be stale. During that window the
        // local approximation (computeTotalsLocal) is the fresher number; once the
        // save lands, flushSave bumps `totalsVersion` and we re-fetch authoritatively.
        if (pendingPatchRef.current || isSavingRef.current) return;
        let cancel = false;
        Promise.all(drafts.map((d) =>
            api.get(`/parts/${partId}/drafts/${d.draft_id}/totals`)
                .then((r) => [d.draft_id, r.data])
                .catch(() => [d.draft_id, null])
        )).then((entries) => {
            if (cancel) return;
            setTotalsByDraft((prev) => {
                const next = { ...prev };
                entries.forEach(([did, t]) => { if (t) next[did] = { ...next[did], ...t }; });
                return next;
            });
        });
        return () => { cancel = true; };
    }, [partId, drafts, totalsVersion, part?.annual_volume, part?.project_years]);

    // Fetch children rollup (used in chart/KPIs to show parent + sub-assemblies)
    useEffect(() => {
        if (!part?.is_assembly || !activeDraftId) { setChildrenRollup(null); return; }
        let cancel = false;
        api.get(`/parts/${partId}/drafts/${activeDraftId}/totals`).then((r) => {
            if (cancel) return;
            setChildrenRollup(r.data?.children_rollup || null);
        }).catch(() => { /* silent */ });
        return () => { cancel = true; };
    }, [partId, part?.is_assembly, part?.child_parts, activeDraftId, drafts]);

    const activeDraft = drafts.find((d) => d.draft_id === activeDraftId);

    const createDraft = async () => {
        const name = newDraftName.trim() || `${activeMode === "zbc" ? "Actual" : "Quote"} ${drafts.filter((d) => (d.mode || "quotation") === activeMode).length + 1}`;
        try {
            const res = await api.post(`/parts/${partId}/drafts`, {
                name,
                mode: activeMode,
                frozen_price: activeMode === "zbc" ? (Number(part?.target_price) || 0) : 0,
                frozen_price_type: "target",
                materials: [], processes: [], outsource: [], investments: [],
                amortise_investment: false,
                overhead: {
                    packing: 0, transportation: 0,
                    icc_mode: "value", icc_value: 0, icc_pct: 0,
                    sga_mode: "value", sga_value: 0, sga_pct: 0,
                    rejection_mode: "pct", rejection_value: 0, rejection_pct: 0,
                    tool_maintenance_mode: "pct", tool_maintenance_value: 0, tool_maintenance_pct: 0,
                    profit_mode: "pct", profit_pct: 0, profit_value: 0,
                },
            });
            setDrafts([...drafts, res.data]);
            setActiveDraftId(res.data.draft_id);
            setNewDraftOpen(false); setNewDraftName("");
            toast.success("Draft created");
        } catch { toast.error("Failed"); }
    };

    // Coerce a value to a finite number; empty / invalid → 0. This guarantees the
    // backend Pydantic float fields never receive an empty string which would 422.
    const toNum = (v) => {
        if (v === "" || v === null || v === undefined) return 0;
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };
    const sanitizeLine = (line, numericKeys) => {
        const out = { ...line };
        numericKeys.forEach((k) => { out[k] = toNum(out[k]); });
        return out;
    };
    const sanitizeMaterials = (arr) => (arr || []).map((m) => sanitizeLine(m, ["price_per_unit", "quantity", "gross_weight", "scrap_weight", "net_weight", "qty", "scrap_pct"]));
    const sanitizeBop       = (arr) => (arr || []).map((b) => sanitizeLine(b, ["price_per_unit", "quantity", "gross_weight", "net_weight", "scrap_weight"]));
    const sanitizeProc      = (arr) => (arr || []).map((p) => sanitizeLine(p, ["mhr_rs_per_hr", "cycle_time_sec", "quantity", "manpower_count", "manpower_rate_per_hr_total"]));
    const sanitizeOut       = (arr) => (arr || []).map((o) => sanitizeLine(o, ["price_per_unit", "price_per_piece", "quantity", "gross_weight"]));
    const sanitizeInv       = (arr) => (arr || []).map((i) => sanitizeLine(i, ["value"]));
    const sanitizeOverhead  = (o) => {
        const out = { ...(o || {}) };
        ["packing", "transportation", "icc_value", "icc_pct", "sga_value", "sga_pct", "rejection_value", "rejection_pct", "tool_maintenance_value", "tool_maintenance_pct", "profit_pct", "profit_value"].forEach((k) => { out[k] = toNum(out[k]); });
        return out;
    };

    // Debounced save coalesces rapid-fire updates (typing/uom-changes) into a
    // single PUT. Falls back to immediate save on flush() or unmount.
    const saveTimerRef = useRef(null);
    const pendingPatchRef = useRef(null);
    const isSavingRef = useRef(false);

    const flushSave = useCallback(async () => {
        if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
        if (!pendingPatchRef.current || !activeDraft) return;
        const updated = pendingPatchRef.current;
        pendingPatchRef.current = null;
        isSavingRef.current = true;
        const payload = {
            name: updated.name,
            mode: updated.mode || "quotation",
            frozen_price: toNum(updated.frozen_price),
            frozen_price_type: updated.frozen_price_type || "target",
            materials: sanitizeMaterials(updated.materials),
            bop: sanitizeBop(updated.bop),
            processes: sanitizeProc(updated.processes),
            outsource: sanitizeOut(updated.outsource),
            investments: sanitizeInv(updated.investments),
            amortise_investment: !!updated.amortise_investment,
            overhead: sanitizeOverhead(updated.overhead),
            direct_cost_mode: !!updated.direct_cost_mode,
            direct_rm_total: toNum(updated.direct_rm_total),
            direct_bop_total: toNum(updated.direct_bop_total),
            direct_process_total: toNum(updated.direct_process_total),
            direct_outsource_total: toNum(updated.direct_outsource_total),
            child_draft_overrides: updated.child_draft_overrides || {},
        };
        const url = `/parts/${partId}/drafts/${updated.draft_id}`;
        // axios.api globally retries 3x on transient (502/503/504/520-524 + Cloudflare HTML
        // error pages + network blips). If we still land here, it's a real, persistent failure.
        try {
            await api.put(url, payload);
            // Saved: re-fetch authoritative backend totals for all drafts so the
            // costing sheet, chart and Compare dialog reflect the persisted values.
            setTotalsVersion((v) => v + 1);
        }
        catch (e) {
            const status = e?.response?.status || 0;
            const detail = e?.response?.data?.detail;
            const isTransient = !status || [502, 503, 504, 408, 520, 521, 522, 523, 524].includes(status);
            const msg = typeof detail === "string"
                ? detail
                : Array.isArray(detail)
                    ? detail.map((d) => `${d.loc?.join?.(".") || ""}: ${d.msg}`).join(" · ")
                    : status === 401 ? "Session expired — please sign in again."
                    : (isTransient ? "Network is jittery — your changes are kept and will sync once the connection is back." : (e?.message || "Could not save changes"));
            console.error("[saveActiveDraft] error after retries:", status, e?.response?.data || e);
            // Re-queue the payload so the next save attempt (typing again / unmount flush) picks it up.
            if (isTransient) {
                pendingPatchRef.current = { ...(pendingPatchRef.current || {}), ...updated };
                // Schedule a retry of the pending payload in 8 seconds
                if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
                saveTimerRef.current = setTimeout(() => { flushSave(); }, 8000);
                toast.warning(`Re-trying save · ${msg}`, { duration: 4000 });
            } else {
                toast.error(`Save failed: ${msg}`);
            }
        }
        isSavingRef.current = false;
    }, [activeDraft, partId]);

    const saveActiveDraft = (patch) => {
        if (!activeDraft) return;
        // Merge optimistic update into the latest queued payload + state
        const prev = pendingPatchRef.current || activeDraft;
        const merged = { ...prev, ...patch };
        pendingPatchRef.current = merged;
        // Reflect immediately in UI so the user feels instant feedback
        setDrafts((cur) => cur.map((d) => d.draft_id === activeDraft.draft_id ? { ...d, ...patch } : d));
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => { flushSave(); }, 350);
    };

    // Flush on unmount + on tab/window hide so we never lose edits
    useEffect(() => {
        const onBlur = () => { if (pendingPatchRef.current) flushSave(); };
        window.addEventListener("beforeunload", onBlur);
        document.addEventListener("visibilitychange", onBlur);
        return () => {
            window.removeEventListener("beforeunload", onBlur);
            document.removeEventListener("visibilitychange", onBlur);
            if (pendingPatchRef.current) flushSave();
        };
    }, [flushSave]);

    const deleteDraft = async (id) => {
        if (!window.confirm("Delete this draft?")) return;
        await api.delete(`/parts/${partId}/drafts/${id}`);
        const newList = drafts.filter((d) => d.draft_id !== id);
        setDrafts(newList);
        setActiveDraftId(newList[0]?.draft_id || null);
        toast.success("Deleted");
    };

    const renameDraft = async (newName) => { if (!activeDraft) return; await saveActiveDraft({ name: newName }); };
    const downloadExcel = () => {
        if (!activeDraft) return;
        window.open(`${API}/parts/${partId}/drafts/${activeDraft.draft_id}/export`, "_blank");
    };

    const duplicateActiveDraft = async () => {
        if (!activeDraft) return;
        try {
            const res = await api.post(`/parts/${partId}/drafts/${activeDraft.draft_id}/duplicate`);
            setDrafts([...drafts, res.data]);
            setActiveDraftId(res.data.draft_id);
            toast.success(`Duplicated as "${res.data.name}"`);
        } catch { toast.error("Duplicate failed"); }
    };

    const selectForProposal = async () => {
        if (!activeDraft) return;
        try {
            await api.post(`/parts/${partId}/select-draft`, { draft_id: activeDraft.draft_id });
            setPart({ ...part, selected_draft_id: activeDraft.draft_id });
            toast.success(`"${activeDraft.name}" selected for business proposal`);
        } catch { toast.error("Failed to select draft"); }
    };

    if (!part) return <div className="p-8 text-zinc-500">Loading…</div>;

    const isSelected = activeDraft && part.selected_draft_id === activeDraft.draft_id;

    return (
        <div>
            <PageHeader
                eyebrow={`${part.serial_no || "—"}${part.part_number ? ` · Part No. ${part.part_number}` : ""}`}
                title={`${part.part_name} · Costing`}
                subtitle={`Customer: ${part.customer_name || "—"}  ·  Annual Vol: ${part.annual_volume?.toLocaleString() || 0}  ·  Target: ₹ ${part.target_price?.toLocaleString() || 0}  ·  Segment: ${part.segment || "—"}  ·  Status: ${part.status || "—"}${part.drawing_revision ? `  ·  Drawing: ${part.drawing_revision}` : ""}`}
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" className="rounded-none" onClick={() => navigate("/dashboard")} data-testid="back-to-parts">
                            <ArrowLeft size={14} className="mr-2" /> Parts
                        </Button>
                        <Button variant="outline" className="rounded-none" onClick={() => setEditOpen(true)} data-testid="edit-part-header-button">
                            <Pencil size={14} className="mr-2" /> Edit Part
                        </Button>
                        <Button
                            variant="outline"
                            className="rounded-none border-[#9C27B0] text-[#9C27B0]"
                            onClick={() => navigate(`/tasks?part_id=${partId}`)}
                            data-testid="part-tasks-button"
                        >
                            Tasks
                        </Button>
                        <Button variant="outline" className="rounded-none" onClick={() => navigate(`/parts/${partId}/proposal`)} data-testid="goto-proposal">
                            <FileText size={14} className="mr-2" /> Proposal · ROI Report
                        </Button>
                        <Button variant="outline" className="rounded-none border-[#FFB703] text-[#7a5400] hover:bg-[#FFF6D9]" onClick={() => navigate(`/parts/${partId}/customer-quote`)} data-testid="goto-customer-quote" title="Customer-facing quote · no cost breakdown">
                            <FileText size={14} className="mr-2" /> Customer Quote
                        </Button>
                        <Button onClick={() => setNewDraftOpen(true)} className="rounded-none bg-[#E63946] hover:bg-[#C92836]" data-testid="new-draft-button">
                            <FilePlus2 size={14} className="mr-2" /> New Draft
                        </Button>
                    </div>
                }
            />

            <div className="px-8 py-6">
                {part.is_child && <WhereUsedBanner partId={partId} navigate={navigate} />}
                {/* Part-level PLM: drawing + revision history (per part, not per draft) */}
                <div className="border border-zinc-200 mb-6" data-testid="plm-strip">
                    <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Part PLM · Drawing & Revision history</span>
                    </div>
                    <DrawingPanel part={part} onReload={loadAll} />
                </div>
                {drafts.length === 0 ? (
                    <div className="border-2 border-dashed border-zinc-300 p-16 text-center">
                        <div className="tag-pill mb-4 inline-flex">No drafts</div>
                        <h2 className="text-2xl font-bold tracking-tight">Create your first draft</h2>
                        <Button onClick={() => setNewDraftOpen(true)} className="mt-6 rounded-none bg-[#002FA7] hover:bg-[#00227B]">
                            <Plus size={16} className="mr-2" /> Create Draft 1
                        </Button>
                    </div>
                ) : (
                    <Tabs value={activeDraftId || ""} onValueChange={setActiveDraftId} data-testid="draft-tabs">
                        {/* Mode switcher (Quotation vs Actual Costing) */}
                        <div className="flex items-center justify-between mb-3">
                            <div className="inline-flex border border-zinc-300" data-testid="costing-mode-switch">
                                {[{ id: "quotation", label: "Quotation" }, { id: "zbc", label: "Actual Costing" }].map((m) => {
                                    const cnt = drafts.filter((d) => (d.mode || "quotation") === m.id).length;
                                    const isActive = activeMode === m.id;
                                    return (
                                        <button
                                            key={m.id}
                                            onClick={() => {
                                                setActiveMode(m.id);
                                                const inMode = drafts.filter((d) => (d.mode || "quotation") === m.id);
                                                setActiveDraftId(inMode[0]?.draft_id || null);
                                            }}
                                            data-testid={`mode-tab-${m.id}`}
                                            className={`px-4 py-1.5 text-xs uppercase tracking-wider font-mono border-r last:border-r-0 border-zinc-300 ${isActive ? "bg-[#09090b] text-white" : "bg-white text-zinc-600 hover:bg-zinc-100"}`}
                                        >
                                            {m.label} <span className="ml-1 opacity-70">({cnt})</span>
                                        </button>
                                    );
                                })}
                            </div>
                            {activeMode === "zbc" && activeDraft && (
                                <div className="flex items-center gap-2 text-xs font-mono" data-testid="zbc-frozen-price-bar">
                                    <span className="text-zinc-500 uppercase tracking-wider">Freeze price as:</span>
                                    <Select value={activeDraft.frozen_price_type || "target"} onValueChange={(v) => saveActiveDraft({ frozen_price_type: v })}>
                                        <SelectTrigger className="rounded-none h-8 w-32" data-testid="zbc-price-type">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="target">Target Price</SelectItem>
                                            <SelectItem value="sales">Sales Price</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        type="number" step="0.01"
                                        value={activeDraft.frozen_price ?? 0}
                                        onChange={(e) => saveActiveDraft({ frozen_price: Number(e.target.value) || 0 })}
                                        className="rounded-none h-8 w-32 font-mono"
                                        placeholder="₹ frozen price"
                                        data-testid="zbc-frozen-price-input"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex items-center justify-between border-b border-zinc-200">
                            <TabsList className="bg-transparent p-0 h-auto">
                                {drafts.filter((d) => (d.mode || "quotation") === activeMode).map((d) => {
                                    const sel = part.selected_draft_id === d.draft_id;
                                    return (
                                        <TabsTrigger
                                            key={d.draft_id}
                                            value={d.draft_id}
                                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-sm"
                                            data-testid={`tab-${d.draft_id}`}
                                        >
                                            {sel && <CheckCircle2 size={12} className="text-[#2A9D8F] mr-1.5" />}
                                            <div className="flex flex-col items-start leading-tight">
                                                <span>{d.name}</span>
                                                {d.serial_no && <span className="text-[8px] font-mono text-zinc-400" data-testid={`tab-serial-${d.draft_id}`}>{d.serial_no}</span>}
                                            </div>
                                        </TabsTrigger>
                                    );
                                })}
                            </TabsList>
                            {activeDraft && (
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant={isSelected ? "default" : "outline"}
                                        className={`rounded-none h-8 ${isSelected ? "bg-[#2A9D8F] hover:bg-[#218377]" : ""}`}
                                        onClick={selectForProposal}
                                        data-testid="select-for-proposal-button"
                                    >
                                        <CheckCircle2 size={14} className="mr-2" />
                                        {isSelected ? "Selected for Proposal" : "Use for Proposal"}
                                    </Button>
                                    <Button variant="outline" className="rounded-none h-8" onClick={() => setCompareOpen(true)} data-testid="compare-drafts-button">
                                        <GitCompare size={14} className="mr-2" /> Compare
                                    </Button>
                                    <Button variant="outline" className="rounded-none h-8" onClick={duplicateActiveDraft} data-testid="duplicate-draft-button">
                                        <Copy size={14} className="mr-2" /> Duplicate
                                    </Button>
                                    <Button variant="outline" className="rounded-none h-8" onClick={downloadExcel} data-testid="export-excel-button">
                                        <Download size={14} className="mr-2" /> Excel
                                    </Button>
                                    <Button variant="outline" className="rounded-none h-8" onClick={() => window.print()} data-testid="export-draft-pdf-button" title={`Print this ${activeDraft?.mode === "zbc" ? "Actual Costing" : "Quotation"} draft as PDF`}>
                                        <Download size={14} className="mr-2" /> PDF
                                    </Button>
                                    <button onClick={() => deleteDraft(activeDraft.draft_id)} className="text-zinc-400 hover:text-[#E63946] p-2">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {drafts.filter((d) => (d.mode || "quotation") === activeMode).map((d) => (
                            <TabsContent key={d.draft_id} value={d.draft_id} className="pt-6 m-0">
                                <DraftEditor
                                    draft={d}
                                    materials={materials}
                                    bopMaster={bopMaster}
                                    vendors={vendors}
                                    machines={machines}
                                    manpower={manpower}
                                    totals={totalsByDraft[d.draft_id]}
                                    part={part}
                                    partId={partId}
                                    onSave={saveActiveDraft}
                                    onRename={renameDraft}
                                    onRefreshMasters={loadAll}
                                />
                            </TabsContent>
                        ))}
                    </Tabs>
                )}
            </div>

            <Dialog open={newDraftOpen} onOpenChange={setNewDraftOpen}>
                <DialogContent className="rounded-none max-w-sm">
                    <DialogHeader><DialogTitle>New Draft</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Draft Name</Label>
                        <Input data-testid="new-draft-name" value={newDraftName} onChange={(e) => setNewDraftName(e.target.value)} placeholder={`Draft ${drafts.length + 1}`} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => setNewDraftOpen(false)}>Cancel</Button>
                        <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" onClick={createDraft} data-testid="create-draft-confirm">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CompareDrafts
                open={compareOpen}
                onOpenChange={setCompareOpen}
                drafts={drafts}
                totalsByDraft={totalsByDraft}
                selectedDraftId={part.selected_draft_id}
                onSelect={async (did) => {
                    await api.post(`/parts/${partId}/select-draft`, { draft_id: did });
                    setPart({ ...part, selected_draft_id: did });
                    toast.success("Selected for proposal");
                }}
                targetPrice={part.target_price || 0}
            />

            <PartEditDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                part={part}
                onSaved={() => loadAll()}
            />

            <AssemblyManager
                open={assemblyOpen}
                onOpenChange={setAssemblyOpen}
                part={part}
                onSaved={() => loadAll()}
            />
        </div>
    );
}


function DrawingPanel({ part, onReload }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const fileRef = React.useRef(null);
    const [uploading, setUploading] = React.useState(false);
    const [historyOpen, setHistoryOpen] = React.useState(false);
    const [notes, setNotes] = React.useState("");
    const [deletingIdx, setDeletingIdx] = React.useState(null);
    const history = part.drawing_history || [];
    const onPick = async (e) => {
        const f = (e.target.files || [])[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const u = await api.post(`/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.post(`/parts/${part.part_id}/drawing-revision`, { drawing_path: u.data.path, revision: "", notes });
            toast.success("Drawing revision saved");
            setNotes("");
            if (fileRef.current) fileRef.current.value = "";
            onReload && onReload();
        } catch { toast.error("Drawing upload failed"); }
        finally { setUploading(false); }
    };
    const onDeleteRev = async (origIdx, rev) => {
        if (!window.confirm(`Delete drawing revision "${rev}"?\n\nThis removes the entry from the PLM history and cannot be undone.`)) return;
        setDeletingIdx(origIdx);
        try {
            await api.delete(`/parts/${part.part_id}/drawing-revision/${origIdx}`);
            toast.success(`Revision ${rev} removed`);
            onReload && onReload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete revision");
        } finally {
            setDeletingIdx(null);
        }
    };
    const fileUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
    return (
        <div className="border-t border-zinc-200 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="drawing-panel">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Drawing</span>
            {part.drawing_path ? (
                <>
                    <a href={fileUrl(part.drawing_path)} target="_blank" rel="noreferrer" className="text-xs text-[#002FA7] hover:underline font-medium" data-testid="drawing-active-link">
                        Active: {part.drawing_revision || "Rev A"} ↗
                    </a>
                    <button onClick={() => setHistoryOpen(true)} className="text-[10px] text-zinc-500 hover:text-[#002FA7] underline" data-testid="drawing-history-btn">
                        history ({history.length})
                    </button>
                </>
            ) : (
                <span className="text-xs text-zinc-400 italic">No drawing yet — upload the first revision to start the PLM trail.</span>
            )}
            <input ref={fileRef} type="file" onChange={onPick} className="hidden" data-testid="drawing-file-input" />
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Revision notes (optional)" className="h-7 rounded-none w-64 ml-auto" data-testid="drawing-notes-input" />
            <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="drawing-upload-btn">
                {uploading ? "Uploading…" : "+ New revision"}
            </Button>
            <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
                <DialogContent className="rounded-none max-w-lg">
                    <DialogHeader><DialogTitle>Drawing revision history — {part.part_name}</DialogTitle></DialogHeader>
                    <div className="divide-y divide-zinc-200" data-testid="drawing-history-list">
                        {history.length === 0 && <div className="text-xs text-zinc-400 italic py-4 text-center">No revisions yet.</div>}
                        {[...history].reverse().map((h, i) => {
                            const origIdx = history.length - 1 - i;  // reversed → original index
                            return (
                                <div key={origIdx} className="py-2 flex items-start gap-3 text-xs" data-testid={`drawing-history-row-${i}`}>
                                    <span className="font-mono font-semibold text-[#002FA7]">{h.rev}</span>
                                    <div className="flex-1">
                                        <div className="font-mono text-[10px] text-zinc-500">{(h.uploaded_at || "").slice(0, 19).replace("T", " ")}</div>
                                        {h.notes && <div className="text-zinc-700 mt-0.5">{h.notes}</div>}
                                    </div>
                                    {h.path && <a href={fileUrl(h.path)} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline">view ↗</a>}
                                    {isAdmin && (
                                        <button
                                            onClick={() => onDeleteRev(origIdx, h.rev)}
                                            disabled={deletingIdx === origIdx}
                                            className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-[#E63946] disabled:opacity-50"
                                            data-testid={`drawing-history-delete-${i}`}
                                            title="Delete this revision (admin only)"
                                        >
                                            {deletingIdx === origIdx ? "…" : "delete"}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {isAdmin && history.length > 0 && (
                        <div className="text-[10px] text-zinc-400 font-mono italic border-t border-zinc-100 pt-2 mt-2">
                            Admin only: deleting the current active revision auto-promotes the newest remaining entry.
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function WhereUsedBanner({ partId, navigate }) {
    const [parents, setParents] = React.useState([]);
    React.useEffect(() => {
        let cancel = false;
        api.get(`/parts/${partId}/where-used`).then((r) => { if (!cancel) setParents(r.data || []); }).catch(() => { });
        return () => { cancel = true; };
    }, [partId]);
    if (!parents.length) return null;
    return (
        <div className="border-l-4 border-[#FFB703] bg-[#FFFBEB] px-4 py-3 mb-4 text-xs" data-testid="where-used-banner">
            <div className="font-semibold text-zinc-700 uppercase tracking-wider text-[10px] mb-1">Where used · {parents.length} assembly{parents.length > 1 ? "s" : ""} consume this child</div>
            <div className="flex flex-wrap gap-2">
                {parents.map((p) => (
                    <button
                        key={p.part_id}
                        onClick={() => navigate(`/parts/${p.part_id}/costing`)}
                        className="px-2 py-1 bg-white border border-zinc-200 hover:border-[#002FA7] hover:text-[#002FA7] font-mono"
                        title={`${p.qty_in_parent} per parent · ${p.customer_name || "—"} · ${p.status || ""}`}
                        data-testid={`where-used-${p.part_id}`}
                    >
                        {p.part_name}{p.part_number ? ` (${p.part_number})` : ""} <span className="text-zinc-400">× {p.qty_in_parent}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}
